import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { Amorce, AmorcePayload, AmorcesService } from '../../../services/amorces.service';
import { Cible, CiblesService } from '../../../services/cibles.service';

@Component({
  selector: 'app-couples-amorces',
  imports: [CommonModule, FormsModule],
  templateUrl: './couples-amorces.component.html',
  styleUrl: './couples-amorces.component.scss'
})
export class CouplesAmorcesComponent implements OnInit {
  protected pageMode: 'creer' | 'modifier' | 'supprimer' = 'supprimer';
  protected amorces: Amorce[] = [];
  protected cibles: Cible[] = [];

  protected selectedId = '';
  protected deletingAmorceId: number | null = null;

  protected nouveauCode = '';
  protected nouveauNomOfficiel = '';
  protected nouvelleCibleId = '';
  protected nouvelleLongueur: number | null = null;
  protected nouveauForward = '';
  protected nouveauReverse = '';

  protected codeEdition = '';
  protected nomOfficielEdition = '';
  protected cibleIdEdition = '';
  protected longueurEdition: number | null = null;
  protected forwardEdition = '';
  protected reverseEdition = '';

  protected loading = false;
  protected loadingList = false;
  protected loadingCibles = false;
  protected success = '';
  protected error = '';

  constructor(
    private readonly amorcesService: AmorcesService,
    private readonly ciblesService: CiblesService,
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.syncPageMode();
    this.router.events.subscribe((event: unknown) => {
      if (event instanceof NavigationEnd) {
        this.syncPageMode();
        this.loadAmorces();
      }
    });

    this.loadCibles();
    this.loadAmorces();
  }

  protected get showCreateSection(): boolean {
    return this.pageMode === 'creer';
  }

  protected get showEditSection(): boolean {
    return this.pageMode === 'modifier';
  }

  protected get showListSection(): boolean {
    return this.pageMode === 'modifier' || this.pageMode === 'supprimer';
  }

  protected get showDeleteActionsInTable(): boolean {
    return this.pageMode === 'supprimer';
  }

  protected createAmorce(): void {
    if (!this.nouveauCode.trim()) {
      this.error = 'Le code est requis.';
      return;
    }

    const parsedLongueur = this.parseLongueur(this.nouvelleLongueur);
    if (parsedLongueur === undefined) {
      this.error = 'La longueur en paires de bases doit être un entier positif ou nul.';
      return;
    }

    this.loading = true;
    this.error = '';
    this.success = '';

    const payload: AmorcePayload = {
      code: this.nouveauCode.trim(),
      nom_officiel: this.toNullableText(this.nouveauNomOfficiel),
      cible_id: this.toNullableNumber(this.nouvelleCibleId),
      longueur: parsedLongueur,
      forward: this.toNullableText(this.nouveauForward),
      reverse: this.toNullableText(this.nouveauReverse),
    };

    this.zone.run(() => {
      this.amorcesService.create(payload).subscribe({
        next: () => {
          this.success = 'Couple d\'amorces créé avec succès.';
          this.resetCreateForm();
          this.loadAmorces();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la création: ${this.getErrorMessage(err)}`;
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  protected onSelectChange(): void {
    const selected = this.amorces.find((amorce) => amorce.id === Number(this.selectedId));
    this.codeEdition = selected?.code ?? '';
    this.nomOfficielEdition = selected?.nom_officiel ?? '';
    this.cibleIdEdition = selected?.cible_id ? String(selected.cible_id) : '';
    this.longueurEdition = selected?.longueur ?? null;
    this.forwardEdition = selected?.forward ?? '';
    this.reverseEdition = selected?.reverse ?? '';
  }

  protected updateAmorce(): void {
    if (!this.selectedId) {
      this.error = 'Veuillez sélectionner un couple d\'amorces.';
      return;
    }

    if (!this.codeEdition.trim()) {
      this.error = 'Le code est requis.';
      return;
    }

    const parsedLongueur = this.parseLongueur(this.longueurEdition);
    if (parsedLongueur === undefined) {
      this.error = 'La longueur doit être un entier positif ou nul.';
      return;
    }

    this.loading = true;
    this.error = '';
    this.success = '';

    const payload: AmorcePayload = {
      code: this.codeEdition.trim(),
      nom_officiel: this.toNullableText(this.nomOfficielEdition),
      cible_id: this.toNullableNumber(this.cibleIdEdition),
      longueur: parsedLongueur,
      forward: this.toNullableText(this.forwardEdition),
      reverse: this.toNullableText(this.reverseEdition),
    };

    this.zone.run(() => {
      this.amorcesService.update(Number(this.selectedId), payload).subscribe({
        next: () => {
          this.success = 'Couple d\'amorces modifié avec succès.';
          this.loadAmorces();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la modification: ${this.getErrorMessage(err)}`;
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  protected deleteAmorceRow(amorce: Amorce): void {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le couple d'amorces "${amorce.code}" ?`)) {
      return;
    }

    this.deletingAmorceId = amorce.id;
    this.error = '';
    this.success = '';

    this.zone.run(() => {
      this.amorcesService.delete(amorce.id).subscribe({
        next: () => {
          this.success = 'Couple d\'amorces supprimé avec succès.';
          this.loadAmorces();
          if (this.selectedId === String(amorce.id)) {
            this.selectedId = '';
            this.clearEditForm();
          }
          this.deletingAmorceId = null;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la suppression: ${this.getErrorMessage(err)}`;
          this.deletingAmorceId = null;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private syncPageMode(): void {
    const lastPart = this.router.url.split('/').pop() ?? '';
    if (['creer', 'modifier', 'supprimer'].includes(lastPart)) {
      this.pageMode = lastPart as 'creer' | 'modifier' | 'supprimer';
    } else {
      this.pageMode = 'supprimer';
    }
  }

  private loadCibles(): void {
    this.loadingCibles = true;
    this.zone.run(() => {
      this.ciblesService.list().subscribe({
        next: (cibles: Cible[]) => {
          this.cibles = cibles;
          this.loadingCibles = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors du chargement des cibles: ${this.getErrorMessage(err)}`;
          this.loadingCibles = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private loadAmorces(): void {
    this.loadingList = true;
    this.error = '';

    this.zone.run(() => {
      this.amorcesService.list().subscribe({
        next: (amorces: Amorce[]) => {
          this.amorces = amorces;
          if (this.selectedId) {
            this.onSelectChange();
          }
          this.loadingList = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors du chargement: ${this.getErrorMessage(err)}`;
          this.loadingList = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private clearEditForm(): void {
    this.codeEdition = '';
    this.nomOfficielEdition = '';
    this.cibleIdEdition = '';
    this.longueurEdition = null;
    this.forwardEdition = '';
    this.reverseEdition = '';
  }

  private resetCreateForm(): void {
    this.nouveauCode = '';
    this.nouveauNomOfficiel = '';
    this.nouvelleCibleId = '';
    this.nouvelleLongueur = null;
    this.nouveauForward = '';
    this.nouveauReverse = '';
  }

  private toNullableText(value: string): string | null {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  private toNullableNumber(value: string): number | null {
    if (!value) {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private parseLongueur(value: number | null | undefined): number | null | undefined {
    if (value === null || value === undefined) {
      return null;
    }

    if (!Number.isInteger(value) || value < 0) {
      return undefined;
    }

    return value;
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    if (typeof err.error === 'string' && err.error.trim()) {
      return err.error;
    }

    if (err.error?.detail) {
      return err.error.detail;
    }

    if (err.error?.message) {
      return err.error.message;
    }

    return err.statusText || 'Erreur inconnue';
  }
}
